import type { NormPoint, RegionId } from '../types';
import { DESIGN } from '../config';

/**
 * P0A ENGINEERING TEST ASSET — a temporary sea turtle.
 *
 * This is NOT production artwork. Geometry is authored in DESIGN units
 * (1000 x 1000) for readability, then normalized to [0,1]. A real animal will
 * come from the vector asset pipeline documented in ASSET_FORMAT.md.
 *
 * Each region is a set of closed sub-polygons (flippers have two: left + right).
 * Regions may visually overlap (the center shell sits on top of the outer
 * shell); hit-testing order resolves overlaps front-to-back (see hitTest.ts).
 */

export interface RegionShape {
  id: RegionId;
  /** One or more closed polygons, in normalized [0,1] artwork space. */
  polygons: NormPoint[][];
}

function n(x: number, y: number): NormPoint {
  return { x: x / DESIGN.width, y: y / DESIGN.height };
}

/** Build an ellipse polygon (design units) with `segments` vertices. */
function ellipse(
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  segments = 48,
  rotationDeg = 0,
): NormPoint[] {
  const pts: NormPoint[] = [];
  const rot = (rotationDeg * Math.PI) / 180;
  const cos = Math.cos(rot);
  const sin = Math.sin(rot);
  for (let i = 0; i < segments; i++) {
    const a = (i / segments) * Math.PI * 2;
    const ex = Math.cos(a) * rx;
    const ey = Math.sin(a) * ry;
    // rotate then translate
    const dx = ex * cos - ey * sin;
    const dy = ex * sin + ey * cos;
    pts.push(n(cx + dx, cy + dy));
  }
  return pts;
}

// --- Region geometry (design-space authored) --------------------------------

// Outer shell: large rounded body slightly taller than wide, centered.
const outerShell = ellipse(500, 560, 300, 320, 56);

// Center shell panel: smaller ellipse on top of the outer shell.
const centerShell = ellipse(500, 560, 165, 185, 48);

// Head: circle near the top.
const head = ellipse(500, 205, 120, 120, 40);

// Front flippers: two angled ellipses at the upper-left / upper-right.
const frontFlipperL = ellipse(235, 430, 130, 70, 36, 35);
const frontFlipperR = ellipse(765, 430, 130, 70, 36, -35);

// Rear flippers: two smaller angled ellipses at the lower-left / lower-right.
const rearFlipperL = ellipse(300, 800, 95, 60, 32, -30);
const rearFlipperR = ellipse(700, 800, 95, 60, 32, 30);

export const TURTLE_REGIONS: RegionShape[] = [
  { id: 'outer_shell', polygons: [outerShell] },
  { id: 'center_shell', polygons: [centerShell] },
  { id: 'front_flippers', polygons: [frontFlipperL, frontFlipperR] },
  { id: 'rear_flippers', polygons: [rearFlipperL, rearFlipperR] },
  { id: 'head', polygons: [head] },
];

/**
 * Outline geometry. Rendered LAST (on top of all paint) with thick dark strokes.
 * We reuse the region polygons as the outline; each closed polygon is stroked.
 * The renderer also fills these as the white "paper" beneath the paint.
 */
export const TURTLE_OUTLINE: NormPoint[][] = [
  outerShell,
  centerShell,
  head,
  frontFlipperL,
  frontFlipperR,
  rearFlipperL,
  rearFlipperR,
];

/** Union of every region polygon — used to paint the white uncolored "paper". */
export const TURTLE_PAPER_POLYGONS: NormPoint[][] = TURTLE_REGIONS.flatMap((r) => r.polygons);
